import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'

export const SCORE_MIN = 1
export const SCORE_MAX = 5
export const CRITERION_NAME_MAX = 120
export const CRITERION_DESCRIPTION_MAX = 500
export const SORT_ORDER_MAX = 9999
export const COMMENT_MAX = 2000
export const TREND_MAX_QUARTERS = 12

const criterionSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullish(),
  sortOrder: z.number(),
  active: z.boolean(),
  ratingCount: z.number(),
  createdAt: instantSchema.nullish(),
})

export interface RatingCriterion {
  id: string
  name: string
  description: string | null
  sortOrder: number
  active: boolean
  ratingCount: number
  createdAt: string | null
}

function toCriterion(raw: z.infer<typeof criterionSchema>): RatingCriterion {
  return {
    id: raw.id,
    name: raw.name,
    description: raw.description ?? null,
    sortOrder: raw.sortOrder,
    active: raw.active,
    ratingCount: raw.ratingCount,
    createdAt: raw.createdAt ?? null,
  }
}

export interface RatingCriterionDraft {
  name: string
  description: string | null
  sortOrder: number
}

export async function fetchRatingCriteria(activeOnly: boolean): Promise<readonly RatingCriterion[]> {
  const response = await api.get<unknown>('/api/hr/rating-criteria', { params: { activeOnly } })
  return z.array(criterionSchema).parse(response.data).map(toCriterion)
}

export async function createRatingCriterion(draft: RatingCriterionDraft): Promise<RatingCriterion> {
  const response = await api.post<unknown>('/api/hr/rating-criteria', draft)
  return toCriterion(criterionSchema.parse(response.data))
}

export async function updateRatingCriterion(
  id: string,
  draft: RatingCriterionDraft,
): Promise<RatingCriterion> {
  const response = await api.put<unknown>(`/api/hr/rating-criteria/${id}`, draft)
  return toCriterion(criterionSchema.parse(response.data))
}

export async function setRatingCriterionActive(
  id: string,
  active: boolean,
): Promise<RatingCriterion> {
  const action = active ? 'activate' : 'deactivate'
  const response = await api.post<unknown>(`/api/hr/rating-criteria/${id}/${action}`)
  return toCriterion(criterionSchema.parse(response.data))
}

const scoreSchema = z.object({
  criterionId: uuidSchema,
  criterionName: z.string(),
  score: z.number(),
})

const ratingSchema = z.object({
  id: uuidSchema,
  personId: uuidSchema,
  quarter: z.string(),
  ratedOn: z.string(),
  jobTitleId: uuidSchema,
  jobTitleName: z.string(),
  orgNodeId: uuidSchema,
  orgNodeName: z.string(),
  comment: z.string().nullish(),
  ratedBy: uuidSchema,
  ratedByName: z.string(),
  createdAt: instantSchema.nullish(),
  average: z.number().nullish(),
  voided: z.boolean(),
  voidedAt: instantSchema.nullish(),
  voidedByName: z.string().nullish(),
  voidReason: z.string().nullish(),
  scores: z.array(scoreSchema),
})

export interface RatingScore {
  criterionId: string
  criterionName: string
  score: number
}

export interface Rating {
  id: string
  personId: string
  quarter: string
  ratedOn: string
  jobTitleId: string
  jobTitleName: string
  orgNodeId: string
  orgNodeName: string
  comment: string | null
  ratedBy: string
  ratedByName: string
  createdAt: string | null
  average: number | null
  voided: boolean
  voidedAt: string | null
  voidedByName: string | null
  voidReason: string | null
  scores: readonly RatingScore[]
}

function toRating(raw: z.infer<typeof ratingSchema>): Rating {
  return {
    id: raw.id,
    personId: raw.personId,
    quarter: raw.quarter,
    ratedOn: raw.ratedOn,
    jobTitleId: raw.jobTitleId,
    jobTitleName: raw.jobTitleName,
    orgNodeId: raw.orgNodeId,
    orgNodeName: raw.orgNodeName,
    comment: raw.comment ?? null,
    ratedBy: raw.ratedBy,
    ratedByName: raw.ratedByName,
    createdAt: raw.createdAt ?? null,
    average: raw.average ?? null,
    voided: raw.voided,
    voidedAt: raw.voidedAt ?? null,
    voidedByName: raw.voidedByName ?? null,
    voidReason: raw.voidReason ?? null,
    scores: raw.scores,
  }
}

const ratingPageSchema = z.object({
  rows: z.array(ratingSchema),
  total: z.number(),
})

export interface RatingPage {
  rows: readonly Rating[]
  total: number
}

export async function fetchPersonRatings(
  personId: string,
  page: number,
  size: number,
): Promise<RatingPage> {
  const response = await api.get<unknown>(`/api/hr/persons/${personId}/ratings`, {
    params: { page: page - 1, size },
  })
  const parsed = ratingPageSchema.parse(response.data)
  return { rows: parsed.rows.map(toRating), total: parsed.total }
}

export interface RatingDraft {
  quarter: string
  ratedOn: string
  comment: string | null
  scores: readonly { criterionId: string; score: number }[]
}

export async function ratePerson(personId: string, draft: RatingDraft): Promise<Rating> {
  const response = await api.post<unknown>(`/api/hr/persons/${personId}/ratings`, draft)
  return toRating(ratingSchema.parse(response.data))
}

export async function voidRating(id: string, reason: string): Promise<Rating> {
  const response = await api.post<unknown>(`/api/hr/ratings/${id}/void`, { reason })
  return toRating(ratingSchema.parse(response.data))
}

const trendPointSchema = z.object({
  quarter: z.string(),
  average: z.number(),
  ratingCount: z.number(),
})

const trendSchema = z.object({
  from: z.string(),
  to: z.string(),
  average: z.number().nullish(),
  ratingCount: z.number(),
  overall: z.array(trendPointSchema),
  criteria: z.array(
    z.object({
      criterionId: uuidSchema,
      criterionName: z.string(),
      active: z.boolean(),
      points: z.array(trendPointSchema),
    }),
  ),
})

export interface TrendPoint {
  quarter: string
  average: number
  ratingCount: number
}

export interface CriterionTrend {
  criterionId: string
  criterionName: string
  active: boolean
  points: readonly TrendPoint[]
}

export interface RatingTrend {
  from: string
  to: string
  average: number | null
  ratingCount: number
  overall: readonly TrendPoint[]
  criteria: readonly CriterionTrend[]
}

export async function fetchRatingTrend(
  personId: string,
  from: string,
  to: string,
): Promise<RatingTrend> {
  const response = await api.get<unknown>(`/api/hr/persons/${personId}/ratings/trend`, {
    params: { from, to },
  })
  const parsed = trendSchema.parse(response.data)
  return { ...parsed, average: parsed.average ?? null }
}

const QUARTER_PATTERN = /^(\d{4})-Q([1-4])$/

function quarterIndex(quarter: string): number {
  const match = QUARTER_PATTERN.exec(quarter)
  if (match === null) throw new Error(`Chorak «2026-Q3» shaklida emas: ${quarter}`)
  return Number(match[1]) * 4 + Number(match[2]) - 1
}

function quarterAt(index: number): string {
  return `${String(Math.floor(index / 4))}-Q${String((index % 4) + 1)}`
}

export function quarterParts(quarter: string): { year: number; quarter: number } {
  const index = quarterIndex(quarter)
  return { year: Math.floor(index / 4), quarter: (index % 4) + 1 }
}

/** `2026-08-15` → `2026-Q3`. */
export function quarterOfDay(day: string): string {
  return quarterAt(Number(day.slice(0, 4)) * 4 + Math.floor((Number(day.slice(5, 7)) - 1) / 3))
}

export function quarterFirstDay(quarter: string): string {
  const { year, quarter: q } = quarterParts(quarter)
  return `${String(year)}-${String(q * 3 - 2).padStart(2, '0')}-01`
}

/** `2026-Q3`, 4 → `['2025-Q4', '2026-Q1', '2026-Q2', '2026-Q3']`. */
export function quartersEndingAt(last: string, count: number): string[] {
  const end = quarterIndex(last)
  return Array.from({ length: count }, (_, offset) => quarterAt(end - count + 1 + offset))
}

/** Baholash oynasida tanlash mumkin bo'lgan choraklar, yangisi birinchi. */
export function rateableQuarters(hiredAt: string, terminatedAt: string | null, today: string): string[] {
  const last = quarterOfDay(terminatedAt !== null && terminatedAt < today ? terminatedAt : today)
  const first = quarterIndex(quarterOfDay(hiredAt))
  return quartersEndingAt(last, TREND_MAX_QUARTERS)
    .filter((quarter) => quarterIndex(quarter) >= first)
    .reverse()
}

export interface CompareCell {
  quarter: string
  average: number | null
  delta: number | null
}

export interface CompareRow {
  key: string
  criterionName: string | null
  active: boolean
  cells: readonly CompareCell[]
}

export const OVERALL_ROW = 'overall'

function compareRow(
  key: string,
  criterionName: string | null,
  active: boolean,
  points: readonly TrendPoint[],
  quarters: readonly string[],
): CompareRow {
  const byQuarter = new Map(points.map((point) => [point.quarter, point.average]))
  let previous: number | null = null
  const cells = quarters.map((quarter) => {
    const average = byQuarter.get(quarter) ?? null
    const delta =
      average !== null && previous !== null ? Math.round((average - previous) * 100) / 100 : null
    if (average !== null) previous = average
    return { quarter, average, delta }
  })
  return { key, criterionName, active, cells }
}

/** Umumiy o'rtacha va har mezon choraklar kesimida; farq — oldingi baholangan chorakka nisbatan. */
export function compareRows(trend: RatingTrend, quarters: readonly string[]): CompareRow[] {
  return [
    compareRow(OVERALL_ROW, null, true, trend.overall, quarters),
    ...trend.criteria.map((criterion) =>
      compareRow(criterion.criterionId, criterion.criterionName, criterion.active, criterion.points, quarters),
    ),
  ]
}
